<template>
  <CForm @submit.prevent="">
    <transition name="fade">
      <MenssageAlert :messages="messages" />
    </transition>
    <CRow class="mb-4">
      <CCol sm="12">
        <label for="senderSelect" class="font-weight-semi-bold">
          {{ $t(`quick.sms.form.sender.label`) }}
          <span class="text-danger">*</span>
        </label>
        <multiselect
          id="senderSelect"
          v-model="form.sender"
          :multiple="false"
          :options="sendersList || []"
          custom
          :custom-label="(option) => option.label"
          :allow-empty="false"
          :show-labels="false"
          class="multiselect--custom"
          :placeholder="$t(`quick.sms.form.sender.select-one`)"
          :loading="isLoadingSenders"
        >
          <template slot="singleLabel" slot-scope="props">
            {{ form.sender ? form.sender.label : $t(`quick.sms.form.sender.select-one`) }}
          </template>
          <template slot="option" slot-scope="props">
            <CRow>
              <CCol>{{ props.option.label }} </CCol>
            </CRow>
          </template>
        </multiselect>
      </CCol>
    </CRow>
    <CRow>
      <CCol sm="12">
        <label for="contact" class="font-weight-semi-bold">
          {{ $t(`quick.sms.form.contact`) }}
          <span class="font-weight-normal text-gray-500">({{ $t(`quick.sms.form.contact-indicator`) }})</span>
          <span class="text-danger"> *</span>
        </label>
        <CInput
          id="contact"
          :lazy="false"
          :value.sync="$v.form.contact.$model"
          :isValid="checkIfValid('contact')"
          :invalidFeedback="invalidFeedbackContact"
          :disabled="isSubmitLoading"
          @keypress="isNumber($event)"
        />
      </CCol>
    </CRow>
    <CRow>
      <CCol sm="12">
        <label class="font-weight-semi-bold">
          {{ $t(`quick.sms.form.message.label`) }}
          <span class="text-danger">*</span>
        </label>
        <SMSTemplateTextarea
          :is-message-invalid="$v.form.smsMessage.$invalid && $v.form.smsMessage.$dirty"
          :message-invalid-feedback="invalidFeedbackSMSMessage"
          :loaded-message-text="this.form.smsMessage"
          @message-changed="handleSMSMessageChanged"
        />
      </CCol>
    </CRow>
  </CForm>
</template>
<script>
import { validationMixin } from 'vuelidate'
import { required, minLength, maxLength, integer, sameAs } from 'vuelidate/lib/validators'
import services from '@/services.js'
import { getErrorMessage } from '@/utils/errorHandling.js'
import MenssageAlert from '@/components/MenssageAlert'
import SMSTemplateTextarea from '@/components/SMSTemplateTextarea.vue'

const CONTACT_MIN_LENGTH = 5
const CONTACT_MAX_LENGTH = 15

export default {
  name: 'QuickSMSForm',
  mixins: [validationMixin],
  components: { MenssageAlert, SMSTemplateTextarea },
  props: {
    isSubmitLoading: {
      type: Boolean,
      default: () => false,
    },
  },
  validations: {
    form: {
      sender: {
        required,
      },
      contact: {
        required,
        minLength: minLength(CONTACT_MIN_LENGTH),
        maxLength: maxLength(CONTACT_MAX_LENGTH),
        integer,
      },
      smsMessage: {
        required,
      },
    },
    isMsgCharsAvailableNegative: { checked: (value) => value === false },
  },
  data() {
    return {
      sendersList: undefined,
      form: {
        sender: undefined,
        contact: undefined,
        smsMessage: '',
      },
      isMsgCharsAvailableNegative: false,
      messages: [],
      isLoadingSenders: false,
      contactMinLength: CONTACT_MIN_LENGTH,
      contactMaxLength: CONTACT_MAX_LENGTH,
    }
  },
  mounted() {
    this.getSenders()
  },
  computed: {
    invalidFeedbackSMSMessage() {
      if (!this.$v.form.smsMessage.required) return this.$t('quick.sms.form.validations.feedback.required')

      return this.$t('quick.sms.form.validations.feedback.error')
    },
    invalidFeedbackContact() {
      if (!this.$v.form.contact.required) return this.$t('quick.sms.form.validations.feedback.required')
      if (!this.$v.form.contact.minLength) return this.$t('quick.sms.form.validations.feedback.minLength', [this.contactMinLength])
      if (!this.$v.form.contact.maxLength) return this.$t('quick.sms.form.validations.feedback.maxLength', [this.contactMaxLength])

      return this.$t('quick.sms.form.validations.feedback.error')
    },
  },
  methods: {
    async getSenders() {
      try {
        this.isLoadingSenders = true
        const organizationId = this.$route.params.organizationId
        const response = await services.getSendersByOrganizationUserId(organizationId)

        this.sendersList = response.data.data.map((elm) => {
          return { value: elm.senderId, label: elm.sender }
        })
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingSenders = false
      }
    },
    checkIfValid(fieldName) {
      const field = this.$v.form[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !field.$invalid
    },
    isNumber(event) {
      const regex = new RegExp(/\D/g)
      if (regex.test(event.key)) {
        event.preventDefault()
      }
    },
    getForm() {
      return {
        from: this.form.sender.label,
        to: this.form.contact,
        text: this.form.smsMessage,
      }
    },
    clearForm() {
      this.$v.$reset()
      this.form = {
        sender: null,
        contact: null,
        smsMessage: '',
      }
    },
    handleSMSMessageChanged(msgValue, isCharsAvailableNegative) {
      this.form.smsMessage = msgValue
      this.isMsgCharsAvailableNegative = isCharsAvailableNegative
      this.$v.form.smsMessage.$touch()
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        const isValid = !this.$v.$invalid
        this.$emit('validation-change', isValid)
      },
    },
  },
}
</script>
